import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import { useCreateClientMutation } from '@/lib/api/clients';
import { useCreateProjectMutation } from '@/lib/api/engagements';
import {
  deleteEngagementDraft as deleteDraft,
  upsertEngagementDraft,
} from '@/lib/api/engagement-drafts';
import {
  coerceWizardStep,
  emptyWizardState,
  isStepValid,
  isWizardDirty,
  nextStep,
  prevStep,
  stepIndex,
  wizardStepsFor,
  type BaselineDocsStepState,
  type BaseFormationStepState,
  type ClientStepState,
  type ModulesStepState,
  type NewEngagementWizardState,
  type ProjectStepState,
  type WizardStepId,
} from '@/lib/wizard/new-engagement-state';
import { cn } from '@/lib/utils/cn';

import { StepBaselineDocs } from './step-baseline-docs';
import { StepBaseFormation } from './step-base-formation';
import { StepClientProject } from './step-client-project';
import { StepModules } from './step-modules';
import { StepNextSteps, type NextStepRoute } from './step-next-steps';

/**
 * T-0117 / C20-131 — New-engagement wizard.
 *
 * Visual + interaction contract: `design_handoff_caliber_v2/prototype/
 * modals-wizard.js` + `wizard.css`. The shell is a Radix Dialog sized at
 * the prototype's 1080×760 max, with a 240px left rail listing the steps
 * and the active pane on the right; the footer holds Back / Continue (or
 * Open engagement on the last step).
 *
 * Steps — the first two for every engagement, then the flow the modules
 * decide (`wizardStepsFor`):
 *
 *   1. Client & project  — picker + project form. (`step-client-project.tsx`)
 *   2. Modules           — card grid. (`step-modules.tsx`)
 *
 *   With an IV&V module on (Contract Compliance, Health Check, Go-Live
 *   Readiness), the contract-baseline flow, four steps since T-0228:
 *   3. Baseline docs     — chunked upload (T-0040). (`step-baseline-docs.tsx`)
 *   4. AI base formation — the extraction run, started once every document
 *                          is indexed; the last step. (`step-base-formation.tsx`)
 *
 *   With only Discovery and/or Selection on, three steps: no SOW is asked
 *   for and no run is started (the API refuses one for such an engagement).
 *   3. Next steps        — where to start for the modules chosen.
 *                          (`step-next-steps.tsx`)
 *
 * The prototype's fifth step (the methodology proposal review) is gone
 * (E20 / T-0228): assessment defaults apply at creation (T-0219) and
 * proposals are decided in the AI review queue after the wizard closes.
 *
 * Commit timing — the engagement (and, if new, the client) is created on
 * the Continue from step 2. The later steps operate on the live engagement
 * so uploads and the extraction run are real backend writes. The final
 * button just closes + navigates to the engagement overview (or, on the
 * Next steps pane, the screen the person picked); the engagement has been
 * live since step 3.
 *
 * Closing (C2-15):
 *   - ESC, the scrim, the X button and Cancel all close the wizard at once.
 *     Nothing is lost: before the engagement is created, the wizard state
 *     is saved as a draft on every change (and once more on close), and the
 *     portfolio page offers Resume / Discard for it; after the engagement is
 *     created, every later step is a backend write. The old "Discard?"
 *     `window.confirm` is gone — a resumed draft is always dirty, and a
 *     browser that answers the prompt with false trapped the dialog open.
 *
 * Idempotency keys:
 *   - Client create, project create, and base-formation /run each mint a
 *     stable key per wizard open. A network retry within the same open
 *     deduplicates server-side.
 */

export interface NewEngagementWizardProps {
  open: boolean;
  /** Open → true; close → false. */
  onOpenChange: (open: boolean) => void;
  /** Optional preselected client (e.g. from the client-detail "+ New engagement" CTA). */
  preselectClientId?: string;
  /**
   * Resume a saved draft (Tier 3): seeds state from draft_state and keeps
   * writing to the SAME draft row instead of minting a new one, so the
   * eventual engagement mint deletes the right row.
   */
  resumeDraft?: { id: string; state: NewEngagementWizardState };
}

/**
 * A saved draft may carry a step the wizard no longer has, or predate a
 * module card (a draft from before the Discovery card has no `discovery`
 * flag): fill the modules in from the defaults and start it where it can.
 */
function seedState(
  resumeDraft: NewEngagementWizardProps['resumeDraft'],
  preselectClientId: string | undefined,
): NewEngagementWizardState {
  if (!resumeDraft) return emptyWizardState(preselectClientId);
  const modules: ModulesStepState = { ...emptyWizardState().modules, ...resumeDraft.state.modules };
  return {
    ...resumeDraft.state,
    modules,
    step: coerceWizardStep(resumeDraft.state.step, modules),
  };
}

/** Where the wizard can send the person as it closes. */
type WizardExitRoute =
  | '/engagements/$engagementId'
  | '/engagements/$engagementId/proposals'
  | NextStepRoute;

export function NewEngagementWizard({
  open,
  onOpenChange,
  preselectClientId,
  resumeDraft,
}: NewEngagementWizardProps) {
  const navigate = useNavigate();
  const titleId = React.useId();

  const [state, setState] = React.useState<NewEngagementWizardState>(() =>
    seedState(resumeDraft, preselectClientId),
  );

  // Re-seed when the modal opens — never preserve state across closes.
  // A resume open seeds from the saved draft instead of the empty state.
  React.useEffect(() => {
    if (open) setState(seedState(resumeDraft, preselectClientId));
  }, [open, preselectClientId, resumeDraft]);

  // The untouched form for this open, which the draft saves measure "dirty"
  // against: the empty form with the preselected client, so opening the
  // wizard from a client and closing it untouched leaves no draft behind.
  // A resumed draft is measured against the plain empty form, as before.
  const openedState = React.useMemo(
    () => emptyWizardState(resumeDraft ? undefined : preselectClientId),
    [resumeDraft, preselectClientId],
  );

  // Stable keys minted once per open so a network retry within the same
  // wizard session deduplicates server-side. Three writes happen:
  //   - createClient (only when mode === 'new')
  //   - createProject
  //   - runBaseFormation (step 4 of the contract-baseline flow)
  const idemKeyRef = React.useRef<{
    client: string;
    project: string;
    baseFormation: string;
  }>({ client: '', project: '', baseFormation: '' });
  const draftIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (open) {
      idemKeyRef.current = {
        client: crypto.randomUUID(),
        project: crypto.randomUUID(),
        baseFormation: crypto.randomUUID(),
      };
      draftIdRef.current = resumeDraft?.id ?? crypto.randomUUID();
    } else {
      draftIdRef.current = null;
    }
  }, [open, resumeDraft]);

  // T-0128 sub-item C: on every step change while the wizard is open +
  // dirty, persist draft_state. Failures are benign — fire-and-forget.
  React.useEffect(() => {
    if (!open) return;
    if (!draftIdRef.current) return;
    if (!isWizardDirty(state, openedState)) return;
    const id = draftIdRef.current;
    const handle = setTimeout(() => {
      void upsertEngagementDraft(id, state).catch(() => {});
    }, 500);
    return () => clearTimeout(handle);
  }, [open, state, openedState]);

  const createClient = useCreateClientMutation();
  const createProject = useCreateProjectMutation();
  const [serverError, setServerError] = React.useState<Error | ApiError | null>(null);
  const submitting = createClient.isPending || createProject.isPending;

  const setStep = React.useCallback(
    (id: WizardStepId) => setState((s) => ({ ...s, step: id })),
    [],
  );
  const setClient = React.useCallback(
    (patch: Partial<ClientStepState>) =>
      setState((s) => ({ ...s, client: { ...s.client, ...patch } })),
    [],
  );
  const setNewClientField = React.useCallback(
    (key: keyof ClientStepState['newClient'], value: string) =>
      setState((s) => ({
        ...s,
        client: { ...s.client, newClient: { ...s.client.newClient, [key]: value } },
      })),
    [],
  );
  const setProject = React.useCallback(
    (patch: Partial<ProjectStepState>) =>
      setState((s) => ({ ...s, project: { ...s.project, ...patch } })),
    [],
  );
  const setModules = React.useCallback(
    (patch: Partial<ModulesStepState>) =>
      setState((s) => ({ ...s, modules: { ...s.modules, ...patch } })),
    [],
  );
  const setBaselineDocs = React.useCallback(
    (patch: Partial<BaselineDocsStepState>) =>
      setState((s) => ({ ...s, baselineDocs: { ...s.baselineDocs, ...patch } })),
    [],
  );
  // Functional variant for per-row patches from async upload callbacks —
  // reads the LATEST files array instead of a render-time closure (which
  // silently dropped freshly-appended rows when an upload completed).
  const updateBaselineFiles = React.useCallback(
    (updater: (files: BaselineDocsStepState['files']) => BaselineDocsStepState['files']) =>
      setState((s) => ({
        ...s,
        baselineDocs: { ...s.baselineDocs, files: updater(s.baselineDocs.files) },
      })),
    [],
  );
  const setBaseFormation = React.useCallback(
    (patch: Partial<BaseFormationStepState>) =>
      setState((s) => ({ ...s, baseFormation: { ...s.baseFormation, ...patch } })),
    [],
  );

  // Every close vector funnels here. Before the engagement exists the
  // state is kept as a draft (saved once more here, since the debounced
  // save may not have fired yet); after, every step is already a backend
  // write. Either way nothing is lost, so no prompt stands in the way.
  const requestClose = React.useCallback(() => {
    if (submitting) return; // never close mid-commit
    const draftId = draftIdRef.current;
    if (draftId && isWizardDirty(state, openedState)) {
      void upsertEngagementDraft(draftId, state).catch(() => {});
    }
    onOpenChange(false);
  }, [state, openedState, onOpenChange, submitting]);

  /**
   * Mint the engagement at the end of step 2 (Modules → Continue). This
   * runs once; subsequent step transitions are pure navigation. Returns
   * true on success (and advances the cursor); false on failure (the
   * wizard stays on step 2 with `serverError` populated).
   */
  async function mintEngagement(): Promise<boolean> {
    if (submitting) return false;
    if (state.engagement) return true; // already minted; safe re-entrance
    setServerError(null);
    try {
      let clientId = state.client.existingClientId;
      if (state.client.mode === 'new') {
        const created = await createClient.mutateAsync({
          input: {
            name: state.client.newClient.name.trim(),
            client_type: state.client.newClient.client_type,
            primary_contact_name: state.client.newClient.primary_contact_name.trim(),
            primary_contact_email: state.client.newClient.primary_contact_email.trim(),
          },
          idempotencyKey: idemKeyRef.current.client,
        });
        clientId = created.id;
      }
      if (!clientId) {
        throw new Error('No client selected.');
      }

      const project = await createProject.mutateAsync({
        clientId,
        input: {
          name: state.project.name.trim(),
          modules: {
            contract_compliance: state.modules.contract,
            health_check: state.modules.health,
            go_live_readiness: state.modules.golive,
            // Sent as ticked; the API turns Selection on with Discovery (T-0171).
            selection: state.modules.selection,
            discovery: state.modules.discovery,
          },
          start_date: state.project.start_date.trim().length > 0 ? state.project.start_date : null,
          planned_end:
            state.project.planned_end.trim().length > 0 ? state.project.planned_end : null,
          client_pm:
            state.project.client_pm.trim().length > 0 ? state.project.client_pm.trim() : null,
        },
        idempotencyKey: idemKeyRef.current.project,
      });

      setState((s) => ({
        ...s,
        engagement: {
          engagement_id: project.engagement_id,
          project_id: project.id,
          client_id: clientId!,
        },
      }));
      // Once minted, drop the resume draft.
      if (draftIdRef.current) {
        void deleteDraft(draftIdRef.current).catch(() => {});
        draftIdRef.current = null;
      }
      return true;
    } catch (err) {
      setServerError(err instanceof Error ? err : new Error(String(err)));
      return false;
    }
  }

  async function handleContinue() {
    const cur = state.step;
    // The transition out of Modules is the commit point. We mint first,
    // then advance into whichever flow the modules chose.
    if (cur === 'modules' && !state.engagement) {
      const ok = await mintEngagement();
      if (!ok) return;
    }
    setStep(nextStep(cur, state.modules));
  }

  function closeAndGo(to: WizardExitRoute) {
    const eng = state.engagement;
    onOpenChange(false);
    if (eng) {
      void navigate({ to, params: { engagementId: eng.engagement_id } });
    }
  }

  const steps = wizardStepsFor(state.modules);
  const currentIdx = stepIndex(state.step, state.modules);
  const canContinue = isStepValid(state, state.step) && !submitting;
  const isLast = currentIdx === steps.length - 1;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else requestClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1080px,96vw)]',
            'max-h-[92vh] h-[760px]',
            '-translate-x-1/2 -translate-y-1/2 grid-cols-[240px_1fr]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
          )}
          onEscapeKeyDown={(ev) => {
            ev.preventDefault();
            requestClose();
          }}
          onPointerDownOutside={(ev) => {
            ev.preventDefault();
            requestClose();
          }}
        >
          {/* Left rail */}
          <aside className="flex flex-col gap-1 border-r border-ink-100 bg-paper-50 px-5 py-6">
            <div className="mb-3.5 text-meta font-semibold uppercase tracking-wide text-ink-500">
              New engagement
            </div>
            {steps.map((s, idx) => {
              const isActive = s.id === state.step;
              const isDone = idx < currentIdx;
              // Forward jumps require validation; backward is free.
              // Once the engagement is minted, we lock client-project
              // and modules — those steps no longer drive backend
              // writes and re-entering them would confuse the user.
              const canJump =
                isDone && !(state.engagement && (s.id === 'client-project' || s.id === 'modules'));
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={!canJump}
                  onClick={() => canJump && setStep(s.id)}
                  className={cn(
                    'relative flex items-start gap-3 rounded-card px-3 py-2.5 text-left',
                    isActive ? 'border border-ink-100 bg-paper-0' : 'border border-transparent',
                    canJump
                      ? 'cursor-pointer hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600'
                      : 'cursor-default',
                  )}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span
                    className={cn(
                      'inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full border text-meta font-semibold tabular-nums',
                      isActive
                        ? 'border-accent-amber-600 bg-accent-amber-600 text-paper-0'
                        : isDone
                          ? 'border-posture-healthy bg-posture-healthy text-paper-0'
                          : 'border-ink-200 bg-paper-0 text-ink-500',
                    )}
                  >
                    {isDone ? <Check className="h-3 w-3" aria-hidden="true" /> : s.num}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={cn(
                        'block text-body-sm font-medium',
                        isActive ? 'text-ink-900 font-semibold' : 'text-ink-700',
                      )}
                    >
                      {s.name}
                    </span>
                    <span className="mt-0.5 block text-meta text-ink-500">{s.sub}</span>
                  </span>
                </button>
              );
            })}
            <div className="mt-auto border-t border-ink-100 pt-3 text-meta text-ink-500">
              <div className="font-semibold text-ink-700">
                {state.client.mode === 'new'
                  ? state.client.newClient.name || '(new client)'
                  : '(existing client)'}
              </div>
              <div>{state.project.name || '—'}</div>
            </div>
          </aside>

          {/* Right pane */}
          <section className="flex min-h-0 flex-col">
            <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-7 py-5">
              <div className="min-w-0">
                <Dialog.Title id={titleId} className="text-h2 font-semibold text-ink-900">
                  {paneTitle(state.step)}
                </Dialog.Title>
                <Dialog.Description className="mt-1 text-meta text-ink-500">
                  {paneSub(state.step)}
                </Dialog.Description>
              </div>
              <Dialog.Close
                aria-label="Close wizard"
                onClick={(ev) => {
                  ev.preventDefault();
                  requestClose();
                }}
                className={cn(
                  'inline-flex h-8 w-8 flex-none items-center justify-center rounded-card text-ink-500',
                  'hover:bg-paper-100 hover:text-ink-900',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                )}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Dialog.Close>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-7 py-5">
              {state.step === 'client-project' ? (
                <StepClientProject
                  state={state}
                  setClient={setClient}
                  setNewClientField={setNewClientField}
                  setProject={setProject}
                />
              ) : null}
              {state.step === 'modules' ? (
                <StepModules state={state} setModules={setModules} />
              ) : null}
              {state.step === 'baseline-docs' ? (
                <StepBaselineDocs
                  state={state}
                  setBaselineDocs={setBaselineDocs}
                  updateBaselineFiles={updateBaselineFiles}
                />
              ) : null}
              {state.step === 'base-formation' ? (
                <StepBaseFormation
                  state={state}
                  setBaseFormation={setBaseFormation}
                  updateBaselineFiles={updateBaselineFiles}
                  runIdempotencyKey={idemKeyRef.current.baseFormation}
                  onOpenQueue={() => closeAndGo('/engagements/$engagementId/proposals')}
                />
              ) : null}
              {state.step === 'next-steps' ? (
                <StepNextSteps state={state} onGo={closeAndGo} />
              ) : null}

              {serverError ? (
                <div
                  role="alert"
                  className="mt-4 rounded-card border border-cite-broken p-3 text-body-sm text-cite-broken"
                >
                  <strong className="block font-semibold">Create failed.</strong>
                  <span className="text-meta">
                    {serverError instanceof ApiError
                      ? `${serverError.code}: ${serverError.message}`
                      : serverError.message}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
              <span className="text-meta text-ink-500">
                Step {currentIdx + 1} of {steps.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={submitting}
                  className={cn(
                    'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                    'hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                  )}
                >
                  Cancel
                </button>
                {currentIdx > 0 ? (
                  <button
                    type="button"
                    onClick={() => setStep(prevStep(state.step, state.modules))}
                    disabled={
                      submitting ||
                      // Once the engagement exists, the user can't go
                      // back to the create-engagement steps — those are
                      // irrevocable.
                      Boolean(
                        state.engagement &&
                        (prevStep(state.step, state.modules) === 'client-project' ||
                          prevStep(state.step, state.modules) === 'modules'),
                      )
                    }
                    className={cn(
                      'inline-flex h-9 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                      'hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                    )}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    Back
                  </button>
                ) : null}
                {isLast ? (
                  <button
                    type="button"
                    onClick={() => closeAndGo('/engagements/$engagementId')}
                    disabled={!canContinue || submitting || !state.engagement}
                    className={cn(
                      'inline-flex h-9 items-center gap-1.5 rounded-card bg-accent-amber-600 px-3 text-body-sm font-medium text-paper-0',
                      'hover:bg-accent-amber-700 disabled:cursor-not-allowed disabled:opacity-50',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                    )}
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    Open engagement
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleContinue()}
                    disabled={!canContinue}
                    className={cn(
                      'inline-flex h-9 items-center gap-1.5 rounded-card bg-accent-amber-600 px-3 text-body-sm font-medium text-paper-0',
                      'hover:bg-accent-amber-700 disabled:cursor-not-allowed disabled:opacity-50',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                    )}
                  >
                    {submitting ? <Spinner /> : null}
                    Continue
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          </section>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function paneTitle(step: WizardStepId): string {
  switch (step) {
    case 'client-project':
      return 'Client & project';
    case 'modules':
      return 'Modules';
    case 'baseline-docs':
      return 'Baseline documents';
    case 'base-formation':
      return 'AI base formation';
    case 'next-steps':
      return 'Next steps';
  }
}

function paneSub(step: WizardStepId): string {
  switch (step) {
    case 'client-project':
      return 'Create a new engagement — Caliber will provision modules, registers, and AI agents.';
    case 'modules':
      return 'Select which Caliber modules to enable for this engagement.';
    case 'baseline-docs':
      return 'Upload baseline documents now, or skip and add them later.';
    case 'base-formation':
      return 'Caliber reads the uploaded documents and proposes the contract baseline. Proposals are decided in the AI review queue after the wizard closes.';
    case 'next-steps':
      return 'Where to start for the modules you chose. Each row opens that screen.';
  }
}
